import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { UtilityService } from 'src/app/services/utility.service';
import { Router } from '@angular/router';
import { UtilitiesAddComponent } from '../utilities-add/utilities-add.component';
import { MatDialog } from '@angular/material/dialog';
import { BaseComponent } from 'src/app/shared/base.component';
import { takeUntil } from 'rxjs/operators';

interface Utility {
  id: string;
  logo: string;
  name: string;
  state: string;
  phone_number: string;
  active: boolean;
}

@Component({
  selector: 'app-utilities-list',
  templateUrl: './utilities-list.component.html',
  styleUrls: ['./utilities-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UtilitiesListComponent extends BaseComponent implements OnInit {
  searchTerm = '';
  showSettingsDropdown = false;
  showFilterDropdown = false;
  filterOptions = ['Recently Added', 'Status', 'State', 'Name'];
  selectedFilter = 'Total Utilities';
  headingLabel = 'Total Utilities';

  utilities: Utility[] = [];
  filteredUtilities: Utility[] = [];
  totalItems = 0;
  itemsPerPage = 10;
  currentPage = 1;
  error: string | null = null;
  logos: { [key: string]: string } = {};

  constructor(
    private utilityService: UtilityService,
    private router: Router,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadUtility(this.currentPage);
  }

  public addUtility(): void {
    const dialogRef = this.dialog.open(UtilitiesAddComponent, {
      width: '1000px',
      maxHeight: '90vh',
      panelClass: 'custom-dialog-container',
      disableClose: true,
    });
    
    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result) {
          setTimeout(() => {
            this.loadUtility(this.currentPage);
          }, 1000);
        }
      });
  }

  onFilterChange(option: string): void {
    this.selectedFilter = option;
    this.headingLabel = option;
    this.showFilterDropdown = false;
    this.getUtilityList({ 'filter_key': option });
  }

  getUtilityList(param: any): void {
    this.utilityService.getUtilityList(param)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.utilities = data.items;
          this.filteredUtilities = [...this.utilities];
          this.totalItems = data.total_items;
          
          this.utilities.forEach(utility => {
            if (utility.id) {
              this.logos[utility.id] = 'assets/utility_logo.png';
              this.getImageLogo(utility.id);
            }
          });
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error loading Utility:', err);
          this.error = `Error loading Utility: ${err.message}`;
          this.cdr.markForCheck();
        }
      });
  }

  onSearchInput(event: any): void {
    this.searchTerm = event.target.value;
    this.applyLocalFilter();
  }

  private applyLocalFilter(): void {
    const term = this.searchTerm?.trim().toLowerCase();
    if (!term) {
      this.filteredUtilities = [...this.utilities];
    } else {
      this.filteredUtilities = this.utilities.filter(u => 
        (u.name || '').toLowerCase().includes(term) ||
        (u.state || '').toLowerCase().includes(term) ||
        (u.phone_number || '').toString().includes(term)
      );
    }
    this.cdr.markForCheck();
  }

  loadUtility(page: number): void {
    const offset = (page - 1) * this.itemsPerPage;
    const isSearching = !!(this.searchTerm && this.searchTerm.trim());

    const param: any = {
      'filter_key': isSearching ? 'Name' : this.selectedFilter,
      'limit': this.itemsPerPage,
      'offset': offset
    };

    if (isSearching) {
      param['filter_value'] = this.searchTerm.trim();
      param['search'] = this.searchTerm.trim();
    }

    this.currentPage = page;
    this.getUtilityList(param);
  }

  onPageChanged(page: number): void {
    this.loadUtility(page);
  }

  onViewConfig(utility: Utility): void {
    this.router.navigate(['/admin-dash/configuration', utility.id]);
  }

  getImageLogo(utilityId: string): void {
    if (!utilityId) return;
    this.utilityService.loadLogoDataUrl(utilityId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(url => {
        this.logos[utilityId] = url;
        this.cdr.markForCheck();
      });
  }

  onImageError(event: Event): void {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = 'assets/utility_logo.png';
  }

  goBack(): void {
    this.router.navigate(['/admin-dash/dashboard']);
  }
}
